// Meldingen aanzetten voor dit apparaat.
//
// De browser wil de vraag "mag ik je meldingen sturen?" alleen stellen na een klik, en de
// toestemming geldt per apparaat én per browser. Vandaar dat dit een knop is en geen schakelaar
// die automatisch aan gaat: je zet het aan op de telefoon waar je 'm op wilt krijgen.
//
// Wat er technisch gebeurt: de browser maakt een abonnement bij de push-dienst van zijn maker
// (Google, Apple, Mozilla) en geeft ons een adres met twee sleutels. Die bewaren wij, en meer
// hebben we niet nodig — de inhoud van een melding is voor die dienst onleesbaar.

import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { Icoon } from "./Icoon.tsx";

type Stand = "onbekend" | "kan-niet" | "uit" | "aan" | "geweigerd";

/** base64url uit de server naar de bytes die `subscribe` wil. */
function naarBytes(sleutel: string): ArrayBuffer {
  const plat = (sleutel + "=".repeat((4 - (sleutel.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const rauw = atob(plat);
  const bytes = new Uint8Array(rauw.length);
  for (let i = 0; i < rauw.length; i++) bytes[i] = rauw.charCodeAt(i);
  return bytes.buffer;
}

/** Iets herkenbaars om het apparaat in de lijst terug te vinden. */
function apparaatNaam(): string {
  const ua = navigator.userAgent;
  const soort = /Android/i.test(ua) ? "Telefoon" : /iPhone|iPad/i.test(ua) ? "iPhone" : "Computer";
  const browser = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox" : /Safari/i.test(ua) ? "Safari" : "browser";
  return `${soort} · ${browser}`;
}

export function Herinneringen({ opMelding }: { opMelding: (tekst: string) => void }) {
  const [stand, setStand] = useState<Stand>("onbekend");
  const [apparaten, setApparaten] = useState<{ id: string; naam: string }[]>([]);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    void (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return setStand("kan-niet");
      if (Notification.permission === "denied") return setStand("geweigerd");
      try {
        const werker = await navigator.serviceWorker.ready;
        const abonnement = await werker.pushManager.getSubscription();
        setStand(abonnement ? "aan" : "uit");
        const uit = await api.pushSleutel();
        setApparaten(uit.apparaten);
      } catch {
        setStand("kan-niet");
      }
    })();
  }, []);

  async function zetAan() {
    setBezig(true);
    try {
      const toestemming = await Notification.requestPermission();
      if (toestemming !== "granted") {
        setStand(toestemming === "denied" ? "geweigerd" : "uit");
        return;
      }
      const { sleutel } = await api.pushSleutel();
      const werker = await navigator.serviceWorker.ready;
      const abonnement = await werker.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: naarBytes(sleutel)
      });
      const uit = await api.meldAanVoorPush(abonnement.toJSON(), apparaatNaam());
      setApparaten(uit.apparaten);
      setStand("aan");
      opMelding("Meldingen staan aan op dit apparaat");
    } catch (fout) {
      opMelding(`Meldingen aanzetten lukte niet: ${(fout as Error).message}`);
    } finally {
      setBezig(false);
    }
  }

  async function zetUit() {
    setBezig(true);
    try {
      const werker = await navigator.serviceWorker.ready;
      const abonnement = await werker.pushManager.getSubscription();
      if (abonnement) {
        const uit = await api.meldAfVoorPush(abonnement.endpoint);
        setApparaten(uit.apparaten);
        await abonnement.unsubscribe();
      }
      setStand("uit");
      opMelding("Meldingen staan uit op dit apparaat");
    } catch (fout) {
      opMelding(`Uitzetten lukte niet: ${(fout as Error).message}`);
    } finally {
      setBezig(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <h3 style={{ fontSize: 15, margin: 0 }}>Herinneringen</h3>
      <p style={{ margin: 0, fontSize: 13, color: "var(--gedempt)" }}>
        Zet een datum achter een taak en je krijgt er een seintje van:{" "}
        <code style={{ fontFamily: "var(--schrift-code)" }}>[ ] offerte nabellen !vrijdag</code>. Ook{" "}
        <code style={{ fontFamily: "var(--schrift-code)" }}>!morgen</code>,{" "}
        <code style={{ fontFamily: "var(--schrift-code)" }}>!25-09</code> en een tijd erachter (
        <code style={{ fontFamily: "var(--schrift-code)" }}>!morgen 14:00</code>) werken; zonder tijd is het
        's ochtends om negen uur. Meldingen zet je per apparaat aan.
      </p>

      {stand === "kan-niet" && (
        <p style={{ margin: 0, fontSize: 13, padding: 10, borderRadius: 10, color: "#8c440f", background: "#fbf0e6" }}>
          Deze browser kan geen meldingen ontvangen. Op een iPhone lukt het alleen als je de app eerst
          via <strong>Deel → Zet op beginscherm</strong> installeert.
        </p>
      )}

      {stand === "geweigerd" && (
        <p style={{ margin: 0, fontSize: 13, padding: 10, borderRadius: 10, color: "#8c440f", background: "#fbf0e6" }}>
          Je hebt meldingen voor deze site geweigerd. Dat kan de app niet zelf terugdraaien — zet het
          in je browser weer aan bij de instellingen voor deze site.
        </p>
      )}

      {(stand === "uit" || stand === "aan") && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {stand === "uit" ? (
            <button className="knop hoofd" disabled={bezig} onClick={() => void zetAan()}>
              <Icoon naam="klok" grootte={15} /> Meldingen aanzetten op dit apparaat
            </button>
          ) : (
            <>
              <button
                className="knop"
                disabled={bezig}
                onClick={async () => {
                  const uit = await api.proefMelding();
                  opMelding(
                    uit.aangekomen > 0
                      ? `Proefmelding verstuurd naar ${uit.aangekomen} apparaat(en)`
                      : "Er staat geen apparaat aangemeld"
                  );
                }}
              >
                Proefmelding sturen
              </button>
              <button className="knop stil" disabled={bezig} onClick={() => void zetUit()}>
                Uitzetten op dit apparaat
              </button>
            </>
          )}
        </div>
      )}

      {apparaten.length > 0 && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--gedempt)" }}>
          Aangemeld: {apparaten.map((a) => a.naam || "naamloos apparaat").join(", ")}
        </p>
      )}
    </section>
  );
}
